import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Divider, Form, Input, message, Row, Select } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import moment from 'moment';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { IListColumn, ISortColumn, PaginationResponse } from 'src/@types/search-params';
import { getAllReportDataPackageNew } from 'src/api/register_and_cancel_and_load_package';
import { getReportDataUsage } from 'src/api/report';
import CGridView from 'src/components/CGridView';
import TableContent from 'src/components/CTable';
import { useAppSelector } from 'src/redux/hook';
import useWindowSize from 'src/hooks/useWindowSize';
import { REGEX_COMMON } from 'src/shared/common/regex';
import { IOrderStatus } from 'src/shared/interface/package_assigned';
import {
  exportExcel,
  formatDate,
  formatMoney,
  formatPhoneNumberV2,
  nonAccentVietnamese,
  parseQueryString,
} from 'src/utils';
import { usePermission } from 'src/hooks/usePermission';

const ReportDataUsagePage = () => {
  const location = useLocation();
  const [_, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const { activeKeyId } = useAppSelector((state) => state.global);
  const size = useWindowSize();
  const [data, setData] = useState<PaginationResponse<any>>();
  const [sortColumn] = useState<ISortColumn>();
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [dataPackage, setDataPackage] = useState<IOrderStatus[]>([]);

  const { hasPermission, PERMISSIONS } = usePermission();

  useEffect(() => {
    fetchGetPackageAssigned();
    form.resetFields();
  }, [activeKeyId]);

  const fetchGetPackageAssigned = async () => {
    await getAllReportDataPackageNew().then((res: any) => {
      if (res) {
        const dataPackage: IOrderStatus[] = [{ value: '', label: 'Tất cả', shortLabel: 'Tất cả' }];
        res?.map((item: any) => {
          dataPackage?.push({
            label: (
              <Row gutter={16}>
                <Col span={12}>{item?.mbfCode ?? ''}</Col>
                <Col span={12}>{item?.mbfName ?? ''}</Col>
              </Row>
            ),
            value: item.mbfCode,
            shortLabel: item.mbfName,
          });
        });
        setDataPackage(dataPackage);
      }
    });
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current >= moment().endOf('day');
  };

  const handleFormSearch = (value: any) => {
    let values = { ...value };
    if (values.dateRange) {
      const fromDate = new Date(values.dateRange[0].format('YYYY-MM-DD'));
      const toDate = new Date(values.dateRange[1].format('YYYY-MM-DD'));
      const rangeDate = Math.ceil((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));
      if (rangeDate > 30) {
        message.warning('Thời gian báo cáo tìm kiếm tối đa là 30 ngày');
        return;
      }
    }
    if (values.activePck) {
      const fromDate = new Date(values.activePck[0].format('YYYY-MM-DD'));
      const toDate = new Date(values.activePck[1].format('YYYY-MM-DD'));
      const rangeDate = Math.ceil((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));
      if (rangeDate > 30) {
        message.warning('Thời gian kích hoạt gói tìm kiếm tối đa là 30 ngày');
        return;
      }
    }
    if (values.activeSub) {
      const fromDate = new Date(values.activeSub[0].format('YYYY-MM-DD'));
      const toDate = new Date(values.activeSub[1].format('YYYY-MM-DD'));
      const rangeDate = Math.ceil((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));
      if (rangeDate > 30) {
        message.warning('Thời gian kích hoạt thuê bao tìm kiếm tối đa là 30 ngày');
        return;
      }
    }
    values = {
      ...values,
      isdn: values.isdn?.trim(),
      FromDate:
        value.dateRange && value?.dateRange[0]
          ? value?.dateRange[0].format('YYYY-MM-DD')
          : value.FromDate ?? undefined,
      ToDate:
        value.dateRange && value?.dateRange[1]
          ? value?.dateRange[1].format('YYYY-MM-DD')
          : value.ToDate ?? undefined,
      ActivePckFromDate:
        value.activePck && value?.activePck[0]
          ? value?.activePck[0].format('YYYY-MM-DD')
          : value.ActivePckFromDate ?? undefined,
      ActivePckToDate:
        value.activePck && value?.activePck[1]
          ? value?.activePck[1].format('YYYY-MM-DD')
          : value.ActivePckToDate ?? undefined,
      ActiveSubFromDate:
        value.activeSub && value?.activeSub[0]
          ? value?.activeSub[0].format('YYYY-MM-DD')
          : value.ActiveSubFromDate ?? undefined,
      ActiveSubToDate:
        value.activeSub && value?.activeSub[1]
          ? value?.activeSub[1].format('YYYY-MM-DD')
          : value.ActiveSubToDate ?? undefined,
      dateRange: undefined,
      activePck: undefined,
      activeSub: undefined,
    };
    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        delete values[key];
      }
    });

    setSearchParams(values);
    fetchDataReport(values);
  };

  const fetchDataReport = async (
    params = {
      isdn: '',
      FromDate: '',
      ToDate: '',
    },
  ) => {
    try {
      const res: any = await getReportDataUsage({
        ...params,
        isdn: params.isdn
          ? REGEX_COMMON.vietNamPhoneNumber.test(params.isdn)
            ? params.isdn.slice(1)
            : params.isdn
          : undefined,
        FromDate: params.FromDate ?? dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
        ToDate: params.ToDate ?? dayjs().format('YYYY-MM-DD'),
      });
      if (res) {
        const flattened = res.results.flatMap((item: any) => {
          const rowSpan = item.groupDatas.length;
          if (rowSpan > 0) {
            return item.groupDatas.map((group: any, idx: number) => ({
              ...item,
              ...group, // merge luôn groupDatas vào cha
              _rowSpan: idx === 0 ? rowSpan : 0, // <- rowSpan ở dòng đầu, 0 cho dòng sau
              isFirstGroupRow: idx === 0, // <- để check render khác nhau nếu muốn
              groupDatasLength: rowSpan, // <- dùng trong table nếu cần
            }));
          } else {
            return { ...item, _rowSpan: 1, isFirstGroupRow: true, groupDatasLength: 1 }; // nếu không có groupDatas thì trả về item với rowSpan = 1
          }
        });
        setData({ ...res, results: flattened });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleExport = () => {
    setLoadingExport(true);
    const link = document.createElement('a');
    const _params = parseQueryString(location.search);
    const newParams = queryString.stringify({
      ..._params,
      FromDate: _params.FromDate ?? dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
      ToDate: _params.ToDate ?? dayjs().format('YYYY-MM-DD'),
    });
    link.target = '_blank';
    link.download = `bao_cao_luu_luong_data_${moment().format('DDMMYYYYHHmmss')}.xlsx`;
    const url =
      process.env.REACT_APP_API_URL +
      '/api/Provider/export/data-usage/enterprise' +
      '?' +
      newParams;
    exportExcel(url)
      .then((res) => {
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        link.click();
      })
      .catch((error) => {
        if (error.status === 403) {
          message.error(`Tài khoản của bạn không có quyền truy cập vào chức năng này`);
        }
      })
      .finally(() => {
        setLoadingExport(false);
      });
  };

  const columns: IListColumn[] = [
    {
      name: 'STT',
      fieldName: 'stt',
      width: 20,
      isHideSort: true,
      onCell: (record: any) => {
        return {
          rowSpan: record._rowSpan,
        };
      },
    },
    {
      name: 'Số thuê bao',
      fieldName: 'isdn',
      isHideSort: true,
      renderSpecialCard: (record) => {
        return <span>{formatPhoneNumberV2(record?.isdn)}</span>;
      },
      onCell: (record: any) => {
        return {
          rowSpan: record._rowSpan,
        };
      },
    },
    {
      name: 'SIM serial',
      fieldName: 'serial',
      isHideSort: true,
      renderSpecialCard: (record) => {
        return <p>{record.serial ?? ''}</p>;
      },
      onCell: (record: any) => {
        return {
          rowSpan: record._rowSpan,
        };
      },
    },
    {
      name: 'Ngày kích hoạt',
      fieldName: 'activeDate',
      width: 180,
      renderSpecialCard: (record) => {
        return <span>{record.activeDate ? formatDate(record.activeDate) : ''}</span>;
      },
      onCell: (record: any) => {
        return {
          rowSpan: record._rowSpan,
        };
      },
    },
    {
      name: 'Trạng thái chặn cắt',
      width: 150,
      fieldName: 'activeStatusIsdn',
      isHideSort: true,
      onCell: (record: any) => {
        return {
          rowSpan: record._rowSpan,
        };
      },
    },
    {
      name: 'Ngày đổi sim',
      width: 150,
      fieldName: 'changeSerialDate',
      isHideSort: true,
      onCell: (record: any) => {
        return {
          rowSpan: record._rowSpan,
        };
      },
      renderSpecialCard: (record) => {
        return <span>{record.changeSerialDate ? formatDate(record.changeSerialDate) : ''}</span>;
      },
    },
    {
      name: 'Số dư tài khoản chính',
      fieldName: 'mainBalance',
      isHideSort: true,
      renderSpecialCard: (record) => {
        return <span>{record.mainBalance ? formatMoney(record.mainBalance, 'đ') : 0}</span>;
      },
      onCell: (record: any) => {
        return {
          rowSpan: record._rowSpan,
        };
      },
    },
    {
      name: 'Gói cước đang dùng',
      fieldName: 'mbfCode',
      isHideSort: true,
    },

    {
      name: 'Ngày kích hoạt gói',
      fieldName: 'activePckDate',
      width: 200,
      renderSpecialCard: (record) => {
        return <span>{record.activePckDate ? record.activePckDate : ''}</span>;
      },
    },
    {
      name: 'Ngày hết hạn gói',
      fieldName: 'expiredPckDate',
      width: 180,

      renderSpecialCard: (record) => {
        return <span>{record.expiredPckDate ? record.expiredPckDate : ''}</span>;
      },
    },
    {
      name: 'Ngày phát sinh lưu lượng data đầu tiên',
      fieldName: 'firstPromDate',
      isHideSort: true,
      width: 200,
      onCell: (record: any) => {
        return {
          rowSpan: record._rowSpan,
        };
      },
      renderSpecialCard: (record) => {
        return <span>{record.firstPromDate ? formatDate(record.firstPromDate) : ''}</span>;
      },
    },
    {
      name: 'Số ngày phát sinh lưu lượng data',
      fieldName: 'countPromDate',
      isHideSort: true,
      width: 200,
      onCell: (record: any) => {
        return {
          rowSpan: record._rowSpan,
        };
      },
    },
    {
      name: 'Lưu lượng data sử dụng (GB/MB)',
      fieldName: 'groupTotalPromVolume',
      isHideSort: true,
      width: 200,
      onCell: (record: any) => {
        return {
          rowSpan: record._rowSpan,
        };
      },
      renderSpecialCard: (record) => {
        return (
          <p>
            {(record.groupTotalPromVolume ?? 0) + (record.groupTotalPayAsYouGoVolume ?? 0) > 0
              ? (
                  (record.groupTotalPromVolume ?? 0) + (record.groupTotalPayAsYouGoVolume ?? 0)
                ).toFixed(2)
              : 0}
            MB
          </p>
        );
      },
    },
    {
      name: 'Tiêu dùng tài khoản chính trong kỳ',
      fieldName: 'averageRevenuePerUser',
      isHideSort: true,
      onCell: (record: any) => {
        return {
          rowSpan: record._rowSpan,
        };
      },
      renderSpecialCard: (record) => {
        return (
          <span>
            {record.averageRevenuePerUser ? formatMoney(record.averageRevenuePerUser, 'đ') : 0}
          </span>
        );
      },
    },
  ];
  return (
    <div>
      <Form
        initialValues={{
          dateRange: [dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day')],
          mbfCode: '',
        }}
        onFinish={handleFormSearch}
        layout={size.width < 768 ? 'vertical' : 'inline'}
        className='gap-2'
        form={form}
      >
        <Form.Item name='dateRange' label='Ngày báo cáo'>
          <DatePicker.RangePicker
            disabledDate={disabledDate}
            placeholder={['Từ ngày', 'đến ngày']}
            format={['DD/MM/YYYY', 'DD/MM/YYYY']}
            allowClear={false}
          />
        </Form.Item>

        <Form.Item name='activeSub' label='Ngày kích hoạt TB'>
          <DatePicker.RangePicker
            disabledDate={disabledDate}
            placeholder={['Từ ngày', 'đến ngày']}
            format={['DD/MM/YYYY', 'DD/MM/YYYY']}
          />
        </Form.Item>
        <Form.Item name='activePck' label='Ngày kích hoạt gói'>
          <DatePicker.RangePicker
            disabledDate={disabledDate}
            placeholder={['Từ ngày', 'đến ngày']}
            format={['DD/MM/YYYY', 'DD/MM/YYYY']}
          />
        </Form.Item>

        <Form.Item name='isdn'>
          <Input
            prefix={<SearchOutlined />}
            placeholder='Thuê bao'
            className='min-w-[300px]'
            allowClear
          />
        </Form.Item>
        <Form.Item name='mbfCode' label='Gói'>
          <Select
            showSearch
            className='min-w-[200px]'
            placeholder='Vui lòng chọn'
            defaultValue={''}
            popupClassName='w-[330px]'
            optionLabelProp='shortLabel'
            options={dataPackage}
            dropdownMatchSelectWidth={false}
            filterOption={(input, option) => {
              return nonAccentVietnamese(
                (option!.shortLabel as unknown as string).toString()?.toLowerCase(),
              ).includes(nonAccentVietnamese(input?.toLowerCase()?.trim()));
            }}
          ></Select>
        </Form.Item>
        <div className='row-button' style={{ justifyContent: 'flex-end', margin: '1.5rem 0' }}>
          <Button type='primary' htmlType='submit'>
            Tìm kiếm
          </Button>
          {hasPermission(PERMISSIONS.REPORT.DATA_USAGE_EXPORT) && (
            <Button loading={loadingExport} onClick={handleExport}>
              Xuất báo cáo
            </Button>
          )}
        </div>
      </Form>
      <div className='mt-6'>
        {size.width < 1024 ? (
          <CGridView
            length={data?.results.length}
            totalRecord={data?.rowCount ?? 0}
            columns={columns}
            title={() => (
              <div className='text-xl font-bold my-3'>Danh sách lưu lượng data thuê bao</div>
            )}
          >
            {data?.results && data?.results.length > 0 ? (
              data?.results.map((item: any) => {
                return (
                  <div key={'data-row-' + item.id}>
                    <Divider className='border-t-black' />

                    <Row className='mb-6 items-center' gutter={[4, 16]}>
                      {columns.map((ele) => {
                        if (ele.fieldName !== 'index' && ele.fieldName !== 'action') {
                          return (
                            <>
                              <Col span={11} className={`font-bold label`}>
                                <div className='flex gap-2 items-center'>{ele.name}</div>
                              </Col>
                              <Col className='break-words text-right' span={13}>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </Col>
                            </>
                          );
                        } else if (ele.fieldName === 'action') {
                          return (
                            <Col key={'column-' + item.id + ele.fieldName} span={24}>
                              <div className='flex gap-6'>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </div>
                            </Col>
                          );
                        } else {
                          return <></>;
                        }
                      })}
                    </Row>
                  </div>
                );
              })
            ) : (
              <p>{'Không có dữ liệu'}</p>
            )}
          </CGridView>
        ) : (
          <TableContent
            bordered
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'Không tìm thấy kết quả' }}
            dataSource={data?.results ?? []}
            columns={columns}
            totalRecord={data?.rowCount ?? 0}
            sortColumn={sortColumn}
            rowSpanFields={[
              'index',
              'isdn',
              'serial',
              'activeDate',
              'mainBalance',
              'activeStatusIsdn',
              'averageRevenuePerUser',
              'countPromDate',
              'firstPromDate',
              'changeSerialDate',
            ]}
            onPageChange={(pageNumber, pageSize) => {
              const pagination = parseQueryString(location.search);
              fetchDataReport({
                ...pagination,
                pageIndex: pageNumber ?? 1,
                pageSize: pageSize ?? 10,
                sortField: pagination.sortField ?? undefined,
                asc: pagination.asc ?? undefined,
              });
            }}
            onSort={(sort: ISortColumn) => {
              const pagination = parseQueryString(location.search);
              fetchDataReport({
                ...pagination,
                pageIndex: pagination.pageIndex ?? 1,
                pageSize: pagination.pageSize ?? 10,
                sortField: sort.fieldName,
                asc:
                  sort.orderType === 'asc' ? true : sort.orderType === 'desc' ? 'false' : undefined,
              });
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ReportDataUsagePage;
